import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import JobTimer from '../../../components/feature/JobTimer';
import MaterialsTracker from '../../../components/feature/MaterialsTracker';

interface Job {
  id: string;
  service_category: string;
  service_subcategory: string;
  customer_name: string;
  customer_address: string;
  customer_postcode: string;
  customer_phone: string;
  customer_email: string;
  customer_latitude: number;
  customer_longitude: number;
  scheduled_date: string | null;
  is_instant: boolean;
  status: string;
  provider_on_route: boolean;
  provider_arrived: boolean;
  job_started_at: string | null;
  job_completed_at: string | null;
  time_spent_minutes: number;
  description: string;
}

interface JobManagementProps {
  providerId: string;
}

interface Material {
  id: string;
  name: string;
  category: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  totalPrice: number;
}

export default function JobManagement({ providerId }: JobManagementProps) {
  const [activeJob, setActiveJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceData, setInvoiceData] = useState({
    hourlyRate: 45,
    materialsCost: 0,
    materialsDescription: ''
  });
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [jobTimerSeconds, setJobTimerSeconds] = useState(0);
  const [materials, setMaterials] = useState<Material[]>([]);

  useEffect(() => {
    loadActiveJob();
    startLocationTracking();

    // Subscribe to job updates
    const subscription = supabase
      .channel(`provider-jobs-${providerId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'jobs',
        filter: `provider_id=eq.${providerId}`
      }, (payload) => {
        if (payload.new) {
          const job = payload.new as Job;
          if (['accepted', 'in_progress'].includes(job.status)) {
            setActiveJob(job);
          } else {
            setActiveJob(null);
          }
        }
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [providerId]);

  const startLocationTracking = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.watchPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCurrentLocation({ lat: latitude, lng: longitude });
          updateProviderLocation(latitude, longitude);
        },
        (error) => {
          let errorMessage = 'Unable to get your location. ';
          
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMessage += 'Please enable location permissions in your browser settings to accept jobs.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage += 'Location information is unavailable. Please check your device settings.';
              break;
            case error.TIMEOUT:
              errorMessage += 'Location request timed out. Please try again.';
              break;
            default:
              errorMessage += 'An unknown error occurred. Please refresh the page and try again.';
          }
          
          console.error('Location error:', {
            code: error.code,
            message: error.message,
            details: errorMessage
          });
          
          alert(errorMessage);
        },
        { 
          enableHighAccuracy: true, 
          maximumAge: 10000,
          timeout: 15000
        }
      );
    } else {
      alert('Geolocation is not supported by your browser. Please use a modern browser to accept jobs.');
    }
  };

  const updateProviderLocation = async (lat: number, lng: number) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/update-provider-location`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            latitude: lat,
            longitude: lng,
          }),
        }
      );
    } catch (err) {
      console.error('Error updating location:', err);
    }
  };

  const loadActiveJob = async () => {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .eq('provider_id', providerId)
        .in('status', ['accepted', 'in_progress'])
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      setActiveJob(data);
      
      // Load saved materials if job exists
      if (data) {
        const savedMaterials = localStorage.getItem(`job_materials_${data.id}`);
        if (savedMaterials) {
          setMaterials(JSON.parse(savedMaterials));
        }
      }
    } catch (err) {
      console.error('Error loading active job:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkOnRoute = async () => {
    if (!activeJob) return;
    setUpdating(true);
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ 
          provider_on_route: true,
          provider_latitude: currentLocation?.lat,
          provider_longitude: currentLocation?.lng
        })
        .eq('id', activeJob.id);

      if (error) throw error;
      await loadActiveJob();
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleMarkArrived = async () => {
    if (!activeJob) return;
    setUpdating(true);
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ provider_arrived: true })
        .eq('id', activeJob.id);

      if (error) throw error;
      await loadActiveJob();
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleStartJob = async () => {
    if (!activeJob) return;
    setUpdating(true);
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ 
          status: 'in_progress',
          job_started_at: new Date().toISOString()
        })
        .eq('id', activeJob.id);

      if (error) throw error;
      await loadActiveJob();
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to start job');
    } finally {
      setUpdating(false);
    }
  };

  const calculateInvoice = () => {
    if (!activeJob?.job_started_at) return null;

    // Use timer seconds if available, otherwise calculate from timestamps
    const actualDurationSeconds = jobTimerSeconds > 0 
      ? jobTimerSeconds 
      : Math.floor((new Date().getTime() - new Date(activeJob.job_started_at).getTime()) / 1000);

    const minutesSpent = Math.floor(actualDurationSeconds / 60);

    // First hour is full, then every 30 minutes
    let billableMinutes = 60; // First hour
    if (minutesSpent > 60) {
      const remainingMinutes = minutesSpent - 60;
      const halfHours = Math.ceil(remainingMinutes / 30);
      billableMinutes += halfHours * 30;
    }

    const laborCost = (billableMinutes / 60) * invoiceData.hourlyRate;
    
    // Calculate materials cost from materials tracker
    const materialsCost = materials.reduce((sum, m) => sum + m.totalPrice, 0);
    
    const subtotal = laborCost + materialsCost;
    const vatAmount = subtotal * 0.20; // 20% VAT
    const platformFee = laborCost * 0.15; // 15% platform fee on labor only
    const totalAmount = subtotal + vatAmount;
    const providerEarnings = laborCost * 0.85 + materialsCost; // Provider gets 85% of labor + 100% of materials

    return {
      actualDurationSeconds,
      minutesSpent,
      billableMinutes,
      laborCost,
      materialsCost,
      subtotal,
      vatAmount,
      platformFee,
      totalAmount,
      providerEarnings
    };
  };

  const handleGenerateInvoice = async () => {
    if (!activeJob) return;

    const invoice = calculateInvoice();
    if (!invoice) return;

    setGeneratingInvoice(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/generate-job-invoice`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            jobId: activeJob.id,
            actualDurationSeconds: invoice.actualDurationSeconds,
            hourlyRate: invoiceData.hourlyRate,
            materials: materials.map(m => ({
              name: m.name,
              category: m.category,
              quantity: m.quantity,
              unit: m.unit,
              unitPrice: m.unitPrice,
              totalPrice: m.totalPrice,
            })),
            laborCost: invoice.laborCost,
            materialsCost: invoice.materialsCost,
            vatAmount: invoice.vatAmount,
            platformFee: invoice.platformFee,
            totalAmount: invoice.totalAmount,
            providerEarnings: invoice.providerEarnings,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to generate invoice');
      }

      alert('Invoice generated successfully! Customer will be notified.');
      
      // Clear saved data
      localStorage.removeItem(`job_timer_${activeJob.id}`);
      localStorage.removeItem(`job_materials_${activeJob.id}`);
      
      setShowInvoiceModal(false);
      await loadActiveJob();

    } catch (err: any) {
      console.error('Error:', err);
      alert(err.message || 'Failed to generate invoice');
    } finally {
      setGeneratingInvoice(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <i className="ri-loader-4-line text-4xl text-[#0B3C5D] animate-spin"></i>
        <p className="mt-4 text-gray-600">Loading job details...</p>
      </div>
    );
  }

  if (!activeJob) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-12 text-center">
        <i className="ri-briefcase-line text-5xl text-gray-400 mb-4"></i>
        <p className="text-gray-600">No active jobs</p>
        <p className="text-sm text-gray-500 mt-2">Accept a job to get started</p>
      </div>
    );
  }

  const invoice = calculateInvoice();

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Active Job</h2>

      <div className="space-y-6">
        {/* Job Details Card */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* Job Header */}
          <div className="flex items-start justify-between mb-6 pb-6 border-b border-gray-200">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{activeJob.service_subcategory}</h3>
              <p className="text-gray-600">{activeJob.service_category}</p>
            </div>
            <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
              activeJob.status === 'in_progress' 
                ? 'bg-purple-100 text-purple-800' 
                : 'bg-blue-100 text-blue-800'
            }`}>
              {activeJob.status === 'in_progress' ? 'In Progress' : 'Accepted'}
            </span>
          </div>

          {/* Customer Info */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <h4 className="font-bold text-gray-900 mb-3">Customer Details</h4>
              <div className="space-y-2">
                <p className="text-gray-900"><strong>Name:</strong> {activeJob.customer_name}</p>
                <p className="text-gray-900"><strong>Phone:</strong> {activeJob.customer_phone}</p>
                <p className="text-gray-900"><strong>Email:</strong> {activeJob.customer_email}</p>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-3">Location</h4>
              <p className="text-gray-900">{activeJob.customer_address}</p>
              <p className="text-gray-600">{activeJob.customer_postcode}</p>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${activeJob.customer_latitude},${activeJob.customer_longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-2 text-[#0B3C5D] hover:underline cursor-pointer"
              >
                <i className="ri-map-pin-line"></i>
                Get Directions
              </a>
            </div>
          </div>

          {activeJob.description && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">Job Description</h4>
              <p className="text-gray-700">{activeJob.description}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {!activeJob.provider_on_route && (
              <button
                onClick={handleMarkOnRoute}
                disabled={updating}
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all font-semibold shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {updating ? 'Updating...' : 'Mark as On Route'}
              </button>
            )}

            {activeJob.provider_on_route && !activeJob.provider_arrived && (
              <button
                onClick={handleMarkArrived}
                disabled={updating}
                className="w-full py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all font-semibold shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {updating ? 'Updating...' : 'Mark as Arrived'}
              </button>
            )}

            {activeJob.provider_arrived && !activeJob.job_started_at && (
              <button
                onClick={handleStartJob}
                disabled={updating}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all font-semibold shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {updating ? 'Starting...' : 'Start Job Timer'}
              </button>
            )}

            {activeJob.job_started_at && activeJob.status === 'in_progress' && (
              <button
                onClick={() => setShowInvoiceModal(true)}
                className="w-full py-3 bg-gradient-to-r from-[#FF6A00] to-[#FF8533] text-white rounded-lg hover:from-[#E55F00] hover:to-[#E57A2E] transition-all font-semibold shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer"
              >
                Complete Job & Generate Invoice
              </button>
            )}
          </div>
        </div>

        {/* Job Timer - Only show when job is in progress */}
        {activeJob.job_started_at && activeJob.status === 'in_progress' && (
          <JobTimer
            jobId={activeJob.id}
            onTimeUpdate={setJobTimerSeconds}
            initialSeconds={0}
          />
        )}

        {/* Materials Tracker - Only show when job is in progress */}
        {activeJob.job_started_at && activeJob.status === 'in_progress' && (
          <MaterialsTracker
            jobId={activeJob.id}
            onMaterialsUpdate={setMaterials}
            initialMaterials={materials}
          />
        )}
      </div>

      {/* Invoice Modal */}
      {showInvoiceModal && invoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Generate Invoice</h3>

            <div className="space-y-6">
              {/* Time Summary */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-3">Time Summary</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Actual Time</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {Math.floor(invoice.minutesSpent / 60)}h {invoice.minutesSpent % 60}m
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Billable Time</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {Math.floor(invoice.billableMinutes / 60)}h {invoice.billableMinutes % 60}m
                    </p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  First hour charged in full, then every 30 minutes
                </p>
              </div>

              {/* Hourly Rate */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Hourly Rate (£)
                </label>
                <input
                  type="number"
                  value={invoiceData.hourlyRate}
                  onChange={(e) => setInvoiceData({ ...invoiceData, hourlyRate: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B3C5D] focus:border-transparent"
                  min="0"
                  step="5"
                />
              </div>

              {/* Materials Summary */}
              {materials.length > 0 && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-bold text-gray-900 mb-3">Materials Used</h4>
                  <div className="space-y-2 mb-3">
                    {materials.map((material) => (
                      <div key={material.id} className="flex justify-between text-sm">
                        <span className="text-gray-700">
                          {material.name} ({material.quantity} {material.unit})
                        </span>
                        <span className="font-semibold text-gray-900">
                          £{material.totalPrice.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="pt-2 border-t border-blue-200 flex justify-between font-semibold">
                    <span>Total Materials</span>
                    <span>£{invoice.materialsCost.toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* Invoice Breakdown */}
              <div className="border-t border-b border-gray-200 py-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Labor Cost</span>
                  <span className="font-semibold text-gray-900">£{invoice.laborCost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Materials</span>
                  <span className="font-semibold text-gray-900">£{invoice.materialsCost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-semibold text-gray-900">£{invoice.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">VAT (20%)</span>
                  <span className="font-semibold text-gray-900">£{invoice.vatAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Platform Fee (15% on labor)</span>
                  <span className="font-semibold text-gray-900">£{invoice.platformFee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xl font-bold pt-3 border-t border-gray-200">
                  <span className="text-gray-900">Total to Customer</span>
                  <span className="text-[#0B3C5D]">£{invoice.totalAmount.toFixed(2)}</span>
                </div>
              </div>

              {/* Provider Earnings */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-green-700 mb-1">Your Earnings</p>
                    <p className="text-xs text-green-600">85% of labor + 100% of materials</p>
                  </div>
                  <p className="text-2xl font-bold text-green-900">£{invoice.providerEarnings.toFixed(2)}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowInvoiceModal(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerateInvoice}
                  disabled={generatingInvoice}
                  className="flex-1 px-6 py-3 bg-[#0B3C5D] text-white rounded-lg hover:bg-[#082A42] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {generatingInvoice ? 'Generating...' : 'Generate Invoice'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}